import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText,
  Send,
  Sparkles,
  Building,
  User,
  Briefcase,
  DollarSign,
  Clock,
  ChevronRight,
  ChevronLeft,
  Edit2,
  Save,
  ExternalLink,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Target,
  Users,
  Building2,
  UserCheck,
  Zap,
  HelpCircle,
  Shield,
  TrendingUp,
  Globe,
  Plus,
  X,
  Copy,
  Palette,
  Upload,
} from "lucide-react";
import { API } from "@/lib/constants";
import FigureWithProvenance from "@/components/FigureWithProvenance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

// Industries with sub-categories
const INDUSTRIES = {
  "Financial Services": [
    "Banking",
    "Insurance",
    "Fintech",
    "Asset Management",
    "Investment Banking",
    "Microfinance",
    "Pension & Retirement",
    "Other Financial Services",
  ],
  "Technology": [
    "Software & SaaS",
    "Hardware & Electronics",
    "IT Services & Consulting",
    "Telecommunications Equipment",
    "Cybersecurity",
    "AI & Data Analytics",
    "Other Technology",
  ],
  "Healthcare": [
    "Hospitals & Health Systems",
    "Pharmaceuticals",
    "Medical Devices",
    "Health Insurance",
    "Diagnostics & Labs",
    "Digital Health",
    "Other Healthcare",
  ],
  "Energy & Utilities": [
    "Oil & Gas",
    "Power Generation",
    "Renewable Energy",
    "Utilities",
    "Mining",
    "Other Energy",
  ],
  "Infrastructure & Construction": [
    "Roads & Highways",
    "Railways",
    "Ports & Maritime Infrastructure",
    "Airports & Aviation Infrastructure",
    "Water & Sanitation",
    "Power Infrastructure",
    "Urban Development",
    "Construction",
    "Property Development",
    "Other Infrastructure",
  ],
  "Aviation & Aerospace": [
    "Commercial Airlines",
    "Cargo Airlines",
    "Airport Operations",
    "Aviation Services (MRO)",
    "Aerospace Manufacturing",
    "Defence & Military Aviation",
    "Other Aviation",
  ],
  "Manufacturing": [
    "Consumer Goods",
    "Industrial Equipment",
    "Automotive",
    "Aerospace & Defence",
    "Food & Beverage",
    "Chemicals",
    "Textiles & Apparel",
    "Other Manufacturing",
  ],
  "Professional Services": [
    "Consulting",
    "Legal",
    "Accounting & Audit",
    "Engineering Services",
    "Recruitment & HR Services",
    "Other Professional Services",
  ],
  "Retail & Consumer": [
    "Retail",
    "E-commerce",
    "Consumer Products",
    "Hospitality & Leisure",
    "Food & Restaurants",
    "Other Retail",
  ],
  "Telecommunications": [
    "Mobile Network Operators",
    "Internet Service Providers",
    "Telecom Infrastructure",
    "Other Telecom",
  ],
  "Government & Public Sector": [
    "Federal/National Government",
    "State/Regional Government",
    "Local Government",
    "Regulatory Bodies",
    "Development Agencies",
    "Parastatals & SOEs",
    "Other Public Sector",
  ],
  "Education": [
    "Higher Education",
    "K-12 Education",
    "EdTech",
    "Corporate Training",
    "Other Education",
  ],
  "Real Estate": [
    "Commercial Real Estate",
    "Residential Development",
    "Property Management",
    "REITs",
    "Other Real Estate",
  ],
  "Transportation & Logistics": [
    "Shipping & Maritime",
    "Road Transport",
    "Rail Transport",
    "Logistics & Supply Chain",
    "Warehousing & Distribution",
    "Other Transport",
  ],
  "Media & Entertainment": [
    "Broadcasting",
    "Publishing",
    "Digital Media",
    "Entertainment & Gaming",
    "Advertising & Marketing",
    "Other Media",
  ],
  "Agriculture & Agribusiness": [
    "Farming & Crop Production",
    "Agro-processing",
    "Agricultural Technology",
    "Livestock & Poultry",
    "Other Agriculture",
  ],
  "Non-Profit & NGO": [
    "International NGOs",
    "Local NGOs",
    "Foundations",
    "Social Enterprises",
    "Other Non-Profit",
  ],
  "Private Equity & Investment": [
    "Private Equity Firms",
    "Venture Capital",
    "Family Offices",
    "Sovereign Wealth Funds",
    "Other Investment",
  ],
  "Other": [],
};

const INDUSTRY_LIST = Object.keys(INDUSTRIES);

// Countries with African countries first
const COUNTRIES = [
  // African countries (primary markets)
  { value: "NG", label: "Nigeria", region: "africa" },
  { value: "GH", label: "Ghana", region: "africa" },
  { value: "KE", label: "Kenya", region: "africa" },
  { value: "ZA", label: "South Africa", region: "africa" },
  { value: "EG", label: "Egypt", region: "africa" },
  { value: "TZ", label: "Tanzania", region: "africa" },
  { value: "UG", label: "Uganda", region: "africa" },
  { value: "RW", label: "Rwanda", region: "africa" },
  { value: "ET", label: "Ethiopia", region: "africa" },
  { value: "SN", label: "Senegal", region: "africa" },
  { value: "CI", label: "Ivory Coast", region: "africa" },
  { value: "CM", label: "Cameroon", region: "africa" },
  { value: "AO", label: "Angola", region: "africa" },
  { value: "MA", label: "Morocco", region: "africa" },
  { value: "TN", label: "Tunisia", region: "africa" },
  { value: "ZM", label: "Zambia", region: "africa" },
  { value: "ZW", label: "Zimbabwe", region: "africa" },
  { value: "BW", label: "Botswana", region: "africa" },
  { value: "MU", label: "Mauritius", region: "africa" },
  // Global markets
  { value: "GB", label: "United Kingdom", region: "global" },
  { value: "US", label: "United States", region: "global" },
  { value: "AE", label: "United Arab Emirates", region: "global" },
  { value: "SA", label: "Saudi Arabia", region: "global" },
  { value: "SG", label: "Singapore", region: "global" },
  { value: "IN", label: "India", region: "global" },
  { value: "DE", label: "Germany", region: "global" },
  { value: "FR", label: "France", region: "global" },
  { value: "NL", label: "Netherlands", region: "global" },
  { value: "CH", label: "Switzerland", region: "global" },
  { value: "OTHER", label: "Other", region: "global" },
];

// Check if country is African
const isAfricanCountry = (countryCode) => {
  const country = COUNTRIES.find(c => c.value === countryCode);
  return country?.region === "africa";
};

const COMPANY_SIZES = [
  "1-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1,000-5,000 employees",
  "5,000+ employees",
];

// Seniority levels - can select multiple
const SENIORITY_LEVELS = [
  { value: "executive", label: "Executive / C-Suite" },
  { value: "senior", label: "Senior Management" },
  { value: "mid", label: "Mid-Level Management" },
  { value: "junior", label: "Junior / Entry Level" },
  { value: "organisation_wide", label: "Organisation Wide" },
];

// Proposal Types - determines structure and content
const PROPOSAL_TYPES = [
  { 
    value: "pilot", 
    label: "Complimentary Pilot", 
    description: "Small-scale diagnostic to demonstrate value and build confidence.",
    icon: "🎯"
  },
  { 
    value: "assessment", 
    label: "Assessment Programme", 
    description: "Structured diagnostic assessment to surface insights and inform decisions.",
    icon: "📊"
  },
  { 
    value: "intervention", 
    label: "Intervention / Transformation", 
    description: "Multi-phase programme for comprehensive organisational change.",
    icon: "🚀"
  },
  { 
    value: "bespoke", 
    label: "Bespoke / Custom", 
    description: "Fully customised engagement. Describe your specific requirements.",
    icon: "✨"
  },
];

const BUYER_TYPES = [
  { value: "CHRO_HR_Director", label: "CHRO / HR Director" },
  { value: "CEO_ExCo", label: "CEO / ExCo Member" },
  { value: "L_and_D", label: "L&D / Talent Development" },
];

// Currencies with Naira and USD first
const CURRENCIES = [
  { value: "NGN", label: "Nigerian Naira (₦)", symbol: "₦" },
  { value: "USD", label: "US Dollar ($)", symbol: "$" },
  { value: "GBP", label: "British Pound (£)", symbol: "£" },
  { value: "EUR", label: "Euro (€)", symbol: "€" },
  { value: "ZAR", label: "South African Rand (R)", symbol: "R" },
  { value: "KES", label: "Kenyan Shilling (KSh)", symbol: "KSh" },
  { value: "GHS", label: "Ghanaian Cedi (GH₵)", symbol: "GH₵" },
  { value: "AED", label: "UAE Dirham (AED)", symbol: "AED" },
];

// Budget ranges by currency
const getBudgetRanges = (currency) => {
  const curr = CURRENCIES.find(c => c.value === currency) || CURRENCIES[0];
  const symbol = curr.symbol;
  
  if (currency === "NGN") {
    return [
      `Under ${symbol}5,000,000`,
      `${symbol}5,000,000 - ${symbol}15,000,000`,
      `${symbol}15,000,000 - ${symbol}50,000,000`,
      `${symbol}50,000,000 - ${symbol}100,000,000`,
      `${symbol}100,000,000+`,
      "To be discussed",
    ];
  } else if (currency === "USD") {
    return [
      `Under ${symbol}25,000`,
      `${symbol}25,000 - ${symbol}50,000`,
      `${symbol}50,000 - ${symbol}100,000`,
      `${symbol}100,000 - ${symbol}250,000`,
      `${symbol}250,000+`,
      "To be discussed",
    ];
  } else if (currency === "GBP") {
    return [
      `Under ${symbol}20,000`,
      `${symbol}20,000 - ${symbol}50,000`,
      `${symbol}50,000 - ${symbol}100,000`,
      `${symbol}100,000 - ${symbol}250,000`,
      `${symbol}250,000+`,
      "To be discussed",
    ];
  } else {
    // Default ranges
    return [
      `Under ${symbol}25,000`,
      `${symbol}25,000 - ${symbol}50,000`,
      `${symbol}50,000 - ${symbol}100,000`,
      `${symbol}100,000 - ${symbol}250,000`,
      `${symbol}250,000+`,
      "To be discussed",
    ];
  }
};

const TIMELINES = [
  "Immediate (within 2 weeks)",
  "Short-term (1-3 months)",
  "Medium-term (3-6 months)",
  "Long-term (6+ months)",
  "Flexible",
];

// Challenge category icons
const CATEGORY_ICONS = {
  leadership_effectiveness: Target,
  team_dynamics: Users,
  culture_trust: Building2,
  hiring_succession: UserCheck,
  change_transformation: Zap,
  coaching_request: Sparkles,
  ambiguous: HelpCircle,
};

// Example challenges by category
const CHALLENGE_EXAMPLES = {
  leadership_effectiveness: [
    "Leaders are not stepping up to the expectations of their roles",
    "Our managers are too operational and struggle to think strategically",
    "Executives lack executive presence and struggle to inspire their teams",
  ],
  team_dynamics: [
    "The leadership team has too much conflict and cannot align on decisions",
    "We have strong individuals but they do not work well as a team",
    "There is a lack of accountability between team members",
  ],
  culture_trust: [
    "People do not speak up in meetings - there is a fear culture",
    "Our engagement scores are declining and we do not know why",
    "The stated values are not being lived in day-to-day behaviour",
  ],
  hiring_succession: [
    "We keep making poor hiring decisions at senior levels",
    "High performers are struggling when promoted to leadership roles",
    "We have significant succession risk in critical roles",
  ],
  change_transformation: [
    "We are scaling rapidly and the organisation is not keeping up",
    "Post-merger integration is challenging - cultures are clashing",
    "Our transformation programme is stalling due to resistance",
  ],
};

export default function ProposalGenerator({ user }) {
  const navigate = useNavigate();
  const { proposalId } = useParams();
  
  // Form state
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    client_name: "",
    contact_person: "",
    industry: "",
    sub_industry: "",
    company_size: "",
    country: "NG", // Default to Nigeria
    challenge_description: "",
    buyer_type: "",
    currency: "NGN", // Default to Naira
    budget_range: "",
    timeline: "",
    additional_notes: "",
    // Proposal type
    proposal_type: "pilot", // pilot, assessment, intervention, bespoke
    // Bespoke requirements (for custom proposals)
    bespoke_requirements: "",
    // Client website for AI research
    client_website: "",
    // Discount percentage (100 = complimentary)
    discount_percentage: 100,
    // Detailed pricing information
    pricing_details: "", // Free-form pricing details
    pricing_line_items: [], // Structured line items: [{description, amount}]
    // RESILIGENT contacts for this engagement
    resiligent_contact_name: "",
    resiligent_contact_title: "",
    resiligent_contact_email: "",
    resiligent_contact_phone: "",
    // Assessment-specific
    participant_count: "",
    programme_duration: "",
    // Intervention-specific  
    intervention_duration_weeks: "",
    // Client logo (stored at client level, displayed here)
    client_logo_url: "",
  });
  
  // Multiple seniority level selection
  const [selectedSeniority, setSelectedSeniority] = useState(["senior"]);
  
  // Multiple category selection
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  // Entry signals for guidance
  const [entrySignals, setEntrySignals] = useState([]);
  
  // Diagnosis preview state (Step 3.5 - review before generation)
  const [diagnosisPreview, setDiagnosisPreview] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [analysingChallenge, setAnalysingChallenge] = useState(false);
  
  // Proposal state
  const [generating, setGenerating] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState("");

  // Item 12/13 — the confirmed rate card, so a pricing line can be PULLED with provenance
  // rather than typed as a bare number with no lineage.
  const [rateCard, setRateCard] = useState(null);
  useEffect(() => {
    fetch(`${API}/pricing/rate-card`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setRateCard).catch(() => {});
  }, []);
  
  // Existing proposals list
  const [existingProposals, setExistingProposals] = useState([]);
  const [showProposalsList, setShowProposalsList] = useState(!proposalId);
  
  // Clone proposal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneTargetClients, setCloneTargetClients] = useState([]);
  const [industryClients, setIndustryClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [cloning, setCloning] = useState(false);
  
  // Gamma export state
  const [showGammaDialog, setShowGammaDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exporting, setExporting] = useState(false);
  const [pollingGamma, setPollingGamma] = useState(false);
  const [gammaConfigured, setGammaConfigured] = useState(false);
  
  // Brand template state
  const [showBrandTemplateDialog, setShowBrandTemplateDialog] = useState(false);
  const [applyingBrandTemplate, setApplyingBrandTemplate] = useState(false);
  const [brandTemplateFile, setBrandTemplateFile] = useState(null);

  useEffect(() => {
    fetchEntrySignals();
    fetchGammaSettings();
    fetchExistingProposals();
    
    if (proposalId) {
      fetchProposal(proposalId);
      setShowProposalsList(false);
    } else {
      // No proposalId - reset to creation mode
      setProposal(null);
      setStep(1);
      setShowProposalsList(true);
    }
  }, [proposalId]);

  // Update currency when country changes
  useEffect(() => {
    if (formData.country === "NG") {
      setFormData(prev => ({ ...prev, currency: "NGN" }));
    } else if (formData.country === "GB") {
      setFormData(prev => ({ ...prev, currency: "GBP" }));
    } else if (formData.country === "US") {
      setFormData(prev => ({ ...prev, currency: "USD" }));
    } else if (formData.country === "ZA") {
      setFormData(prev => ({ ...prev, currency: "ZAR" }));
    } else if (formData.country === "KE") {
      setFormData(prev => ({ ...prev, currency: "KES" }));
    } else if (formData.country === "GH") {
      setFormData(prev => ({ ...prev, currency: "GHS" }));
    }
  }, [formData.country]);

  const fetchExistingProposals = async () => {
    try {
      const response = await fetch(`${API}/proposals`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setExistingProposals(data);
      }
    } catch (error) {
      console.error("Error fetching proposals:", error);
    }
  };

  const fetchEntrySignals = async () => {
    try {
      const response = await fetch(`${API}/diagnosis/entry-signals`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEntrySignals(data.categories || []);
      }
    } catch (error) {
      console.error("Error fetching entry signals:", error);
    }
  };

  const fetchGammaSettings = async () => {
    try {
      const response = await fetch(`${API}/settings/gamma`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.gamma_template_id) {
          setSelectedTemplate(data.gamma_template_id);
          setGammaConfigured(true);
        }
      }
    } catch (error) {
      console.error("Error fetching Gamma settings:", error);
    }
  };

  const fetchProposal = async (id) => {
    try {
      const response = await fetch(`${API}/proposals/${id}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProposal(data);
        setFormData({
          client_name: data.client_name,
          contact_person: data.contact_person,
          industry: data.industry,
          company_size: data.company_size || "",
          country: data.country || "NG",
          challenge_description: data.challenge_description || "",
          seniority_level: data.seniority_level || "senior",
          buyer_type: data.buyer_type || "",
          currency: data.currency || "NGN",
          budget_range: data.budget_range || "",
          timeline: data.timeline || "",
          additional_notes: data.additional_notes || "",
        });
        // If proposal has content, show the proposal view (step 5), otherwise show review (step 4)
        if (data.content && Object.keys(data.content).length > 0) {
          setStep(5);
        } else {
          setStep(4);
        }
      }
    } catch (error) {
      toast.error("Failed to load proposal");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(c => c !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleExampleClick = (example) => {
    setFormData((prev) => ({
      ...prev,
      challenge_description: prev.challenge_description
        ? `${prev.challenge_description}\n\n${example}`
        : example,
    }));
  };

  // Toggle seniority level selection
  const handleSeniorityToggle = (value) => {
    if (value === "organisation_wide") {
      // If selecting organisation wide, select all levels
      if (selectedSeniority.includes("organisation_wide")) {
        setSelectedSeniority([]);
      } else {
        setSelectedSeniority(["organisation_wide", "executive", "senior", "mid", "junior"]);
      }
    } else {
      setSelectedSeniority(prev => {
        const newSelection = prev.includes(value)
          ? prev.filter(s => s !== value && s !== "organisation_wide")
          : [...prev.filter(s => s !== "organisation_wide"), value];
        return newSelection;
      });
    }
  };

  const handleGenerateProposal = async () => {
    if (!formData.client_name || !formData.contact_person || !formData.challenge_description) {
      toast.error("Please fill in client name, contact person, and challenge description");
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error("Please select at least one diagnostic product");
      return;
    }

    if (selectedSeniority.length === 0) {
      toast.error("Please select at least one seniority level");
      return;
    }

    setGenerating(true);
    try {
      // Determine if African context for DISC LENS Africa
      const isAfrican = isAfricanCountry(formData.country);
      
      // Get excluded products (recommended but unticked)
      const allRecommended = diagnosisPreview?.recommended_diagnostics?.map(p => p.name) || [];
      const excludedProducts = allRecommended.filter(p => !selectedProducts.includes(p));
      
      // Determine highest seniority for product selection
      const seniorityPriority = ["executive", "senior", "mid", "junior"];
      const highestSeniority = seniorityPriority.find(s => selectedSeniority.includes(s)) || "senior";
      
      const response = await fetch(`${API}/proposals/generate-intelligent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          seniority_level: highestSeniority,
          seniority_levels: selectedSeniority,
          is_african_context: isAfrican,
          selected_categories: selectedCategories,
          selected_products: selectedProducts,
          excluded_products: excludedProducts,
          diagnosis_preview: diagnosisPreview,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProposal(data);
        setStep(5); // Now step 5 is the final proposal view
        toast.success("Proposal generated successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate proposal");
      }
    } catch (error) {
      toast.error("Failed to generate proposal");
    } finally {
      setGenerating(false);
    }
  };

  // Analyse challenge and get diagnosis preview (Step 4)
  const handleAnalyseChallenge = async () => {
    if (!formData.challenge_description) {
      toast.error("Please describe the client challenge first");
      return;
    }

    if (selectedSeniority.length === 0) {
      toast.error("Please select at least one seniority level");
      return;
    }

    setAnalysingChallenge(true);
    try {
      const isAfrican = isAfricanCountry(formData.country);
      
      // Determine highest seniority for diagnosis
      const seniorityPriority = ["executive", "senior", "mid", "junior"];
      const highestSeniority = seniorityPriority.find(s => selectedSeniority.includes(s)) || "senior";
      
      const response = await fetch(`${API}/diagnosis/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          challenge_text: formData.challenge_description,
          seniority_level: highestSeniority,
          buyer_type: formData.buyer_type,
          is_african_context: isAfrican,
          selected_categories: selectedCategories,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDiagnosisPreview(data);
        // Pre-select all recommended products (user can untick)
        setSelectedProducts(
          data.recommended_diagnostics?.map(p => p.name) || []
        );
        setStep(4); // Move to review step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to analyse challenge");
      }
    } catch (error) {
      toast.error("Failed to analyse challenge");
    } finally {
      setAnalysingChallenge(false);
    }
  };

  // Toggle product selection
  const handleProductToggle = (productName) => {
    setSelectedProducts(prev => {
      if (prev.includes(productName)) {
        return prev.filter(p => p !== productName);
      } else {
        return [...prev, productName];
      }
    });
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    // Handle new structured format
    const content = proposal.content[section];
    if (content && typeof content === 'object' && content.content) {
      setEditContent(content.content);
    } else if (typeof content === 'string') {
      setEditContent(content);
    } else {
      setEditContent("");
    }
  };

  const handleSaveSection = async () => {
    if (!proposal) return;

    // Preserve the structured format if it exists
    const existingSection = proposal.content[editingSection];
    let updatedSectionValue;
    
    if (existingSection && typeof existingSection === 'object' && existingSection.content !== undefined) {
      // New structured format - preserve heading and visual_direction
      updatedSectionValue = {
        ...existingSection,
        content: editContent,
      };
    } else {
      // Legacy format - just use the string
      updatedSectionValue = editContent;
    }

    const updatedContent = {
      ...proposal.content,
      [editingSection]: updatedSectionValue,
    };

    try {
      const response = await fetch(`${API}/proposals/${proposal.proposal_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: updatedContent }),
      });

      if (response.ok) {
        setProposal((prev) => ({ ...prev, content: updatedContent }));
        setEditingSection(null);
        toast.success("Section updated");
      }
    } catch (error) {
      toast.error("Failed to save changes");
    }
  };

  // Clone proposal functions
  const handleOpenCloneModal = async () => {
    setShowCloneModal(true);
    setLoadingClients(true);
    
    try {
      // Fetch clients in the same industry
      const response = await fetch(
        `${API}/clients/by-industry/${encodeURIComponent(formData.industry || proposal?.industry || '')}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const clients = await response.json();
        // Filter out the current client
        const filteredClients = clients.filter(c => c.company_name !== formData.client_name);
        setIndustryClients(filteredClients);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleCloneProposal = async () => {
    if (cloneTargetClients.length === 0) {
      toast.error("Please select at least one client");
      return;
    }

    setCloning(true);
    try {
      const response = await fetch(`${API}/proposals/${proposal.proposal_id}/clone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          target_clients: cloneTargetClients,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Proposal cloned to ${result.cloned_count} client(s)`);
        setShowCloneModal(false);
        setCloneTargetClients([]);
        
        // Optionally navigate to proposals list
        if (result.cloned_count > 0) {
          navigate("/proposals");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to clone proposal");
      }
    } catch (error) {
      toast.error("Failed to clone proposal");
    } finally {
      setCloning(false);
    }
  };

  const toggleClientSelection = (clientId) => {
    setCloneTargetClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleExportToGamma = async () => {
    setExporting(true);
    setShowGammaDialog(false);
    
    try {
      const response = await fetch(`${API}/proposals/${proposal.proposal_id}/export-gamma`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          export_as: "pptx",  // Always export as PPTX for branding
        }),
      });

      if (response.ok) {
        toast.success("Generating presentation with smart graphics...");
        pollGammaStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to export to Gamma");
      }
    } catch (error) {
      toast.error("Failed to export to Gamma");
    } finally {
      setExporting(false);
    }
  };

  const handleQuickExport = () => {
    // Always allow export - theme ID has a default fallback
    handleExportToGamma();
  };

  const pollGammaStatus = async () => {
    if (!proposal) return;
    
    setPollingGamma(true);
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API}/proposals/${proposal.proposal_id}/gamma-status`, {
          credentials: "include",
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === "completed") {
            setPollingGamma(false);
            // Check if auto-branding was successful
            if (data.branded_pptx_url) {
              toast.success("Gamma presentation ready with RESILIGENT branding applied!");
            } else {
              toast.success("Gamma presentation ready");
            }
            fetchProposal(proposal.proposal_id);
            return true;
          } else if (data.status === "error") {
            setPollingGamma(false);
            toast.error("Gamma generation failed");
            return true;
          }
        }
      } catch (error) {
        console.error("Error polling Gamma status:", error);
      }
      return false;
    };

    const interval = setInterval(async () => {
      const done = await checkStatus();
      if (done) {
        clearInterval(interval);
      }
    }, 3000);

    checkStatus();

    setTimeout(() => {
      clearInterval(interval);
      setPollingGamma(false);
    }, 300000);
  };

  // Apply RESILIGENT brand template to uploaded Gamma PPTX
  const handleApplyBrandTemplate = async () => {
    if (!brandTemplateFile) {
      toast.error("Please upload your Gamma PPTX file");
      return;
    }

    if (!brandTemplateFile.name.endsWith('.pptx')) {
      toast.error("Please upload a .pptx file");
      return;
    }

    setApplyingBrandTemplate(true);
    try {
      const formData = new FormData();
      formData.append('file', brandTemplateFile);

      const response = await fetch(`${API}/proposals/${proposal.proposal_id}/apply-brand-template`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        // Download the branded PPTX file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `RESILIGENT_Proposal_${proposal.client_name?.replace(/[^a-zA-Z0-9]/g, '_')}.pptx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("Branded presentation downloaded!");
        setShowBrandTemplateDialog(false);
        setBrandTemplateFile(null);
        
        // Refresh proposal
        fetchProposal(proposal.proposal_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to apply branding");
      }
    } catch (error) {
      console.error("Error applying brand template:", error);
      toast.error("Failed to apply branding");
    } finally {
      setApplyingBrandTemplate(false);
    }
  };

  // Step 1: Client Information
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Proposal Type Selection */}
      <div className="mb-8">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">Proposal Type</h2>
          <p className="text-slate-500 mt-1">Select the type of engagement</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PROPOSAL_TYPES.map((type) => {
            const isSelected = formData.proposal_type === type.value;
            return (
              <label
                key={type.value}
                htmlFor={`proposal-type-${type.value}`}
                className={`relative flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? "bg-indigo-50 border-indigo-500 shadow-md"
                    : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  id={`proposal-type-${type.value}`}
                  name="proposal_type"
                  value={type.value}
                  checked={isSelected}
                  onChange={() => {
                    handleInputChange("proposal_type", type.value);
                    // Auto-set complimentary for pilots
                    handleInputChange("is_complimentary", type.value === "pilot");
                  }}
                  className="sr-only"
                  data-testid={`proposal-type-${type.value}`}
                />
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{type.icon}</span>
                  <span className={`font-semibold ${isSelected ? "text-indigo-700" : "text-slate-800"}`}>
                    {type.label}
                  </span>
                </div>
                <p className={`text-sm ${isSelected ? "text-indigo-600" : "text-slate-500"}`}>
                  {type.description}
                </p>
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle className="w-5 h-5 text-indigo-600" />
                  </div>
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Bespoke Requirements - shown only when Bespoke is selected */}
      {formData.proposal_type === "bespoke" && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
          <Label className="text-purple-800 font-medium mb-2 block">
            Describe Your Bespoke Requirements *
          </Label>
          <Textarea
            value={formData.bespoke_requirements}
            onChange={(e) => handleInputChange("bespoke_requirements", e.target.value)}
            placeholder="Describe what you're looking for in detail. For example:
- What specific outcomes do you want to achieve?
- What format should the proposal take?
- Are there specific methodologies or approaches you want included?
- Any particular focus areas or constraints?
- What does success look like for this engagement?"
            className="mt-1 min-h-[150px] bg-white"
            data-testid="bespoke-requirements-input"
          />
          <p className="text-xs text-purple-600 mt-2">
            Be as specific as possible. The AI will use this to create a fully customised proposal structure.
          </p>
        </div>
      )}

      <div className="border-t pt-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">Client Information</h2>
          <p className="text-slate-500 mt-1">Tell us about the client organisation</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label>Company Name *</Label>
          <Input
            value={formData.client_name}
            onChange={(e) => handleInputChange("client_name", e.target.value)}
            placeholder="e.g., First Bank Nigeria"
            className="mt-1"
            data-testid="client-name-input"
          />
        </div>
        <div>
          <Label>Contact Person *</Label>
          <Input
            value={formData.contact_person}
            onChange={(e) => handleInputChange("contact_person", e.target.value)}
            placeholder="e.g., Dr. Adesola Johnson"
            className="mt-1"
            data-testid="contact-person-input"
          />
        </div>
        <div>
          <Label>Country *</Label>
          <Select
            value={formData.country}
            onValueChange={(v) => handleInputChange("country", v)}
          >
            <SelectTrigger className="mt-1" data-testid="country-select">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">
                Africa
              </div>
              {COUNTRIES.filter(c => c.region === "africa").map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}
              <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 mt-2">
                Global
              </div>
              {COUNTRIES.filter(c => c.region === "global").map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAfricanCountry(formData.country) && (
            <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
              <Globe className="w-3 h-3" />
              African context: DISC LENS Africa will be used
            </p>
          )}
        </div>
        <div>
          <Label>Industry</Label>
          <Select
            value={formData.industry}
            onValueChange={(v) => {
              handleInputChange("industry", v);
              handleInputChange("sub_industry", ""); // Reset sub-industry when industry changes
            }}
          >
            <SelectTrigger className="mt-1" data-testid="industry-select">
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRY_LIST.map((ind) => (
                <SelectItem key={ind} value={ind}>
                  {ind}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {formData.industry && INDUSTRIES[formData.industry]?.length > 0 && (
          <div>
            <Label>Sub-Industry</Label>
            <Select
              value={formData.sub_industry}
              onValueChange={(v) => handleInputChange("sub_industry", v)}
            >
              <SelectTrigger className="mt-1" data-testid="sub-industry-select">
                <SelectValue placeholder="Select sub-industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES[formData.industry].map((subInd) => (
                  <SelectItem key={subInd} value={subInd}>
                    {subInd}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Company Size</Label>
          <Select
            value={formData.company_size}
            onValueChange={(v) => handleInputChange("company_size", v)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((size) => (
                <SelectItem key={size} value={size}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Client Website (for AI research)</Label>
          <Input
            value={formData.client_website}
            onChange={(e) => handleInputChange("client_website", e.target.value)}
            placeholder="e.g., https://www.company.com"
            className="mt-1"
            data-testid="client-website-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            If provided, AI will research the company to personalise the proposal with relevant context about their business, leadership, and recent developments.
          </p>
        </div>
        <div className="md:col-span-2">
          <Label>Client Logo (for branded PPTX)</Label>
          <div className="mt-1 flex items-center gap-4">
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const formDataUpload = new FormData();
                    formDataUpload.append('file', file);
                    try {
                      const response = await fetch(`${API}/upload-client-logo`, {
                        method: 'POST',
                        credentials: 'include',
                        body: formDataUpload,
                      });
                      if (response.ok) {
                        const data = await response.json();
                        handleInputChange("client_logo_url", data.url);
                        toast.success("Logo uploaded successfully");
                      } else {
                        toast.error("Failed to upload logo");
                      }
                    } catch (error) {
                      toast.error("Failed to upload logo");
                    }
                  }
                }}
                className="hidden"
                id="client-logo-upload"
                data-testid="client-logo-upload"
              />
              <label
                htmlFor="client-logo-upload"
                className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 transition-colors"
              >
                <Upload className="w-4 h-4 mr-2 text-slate-500" />
                <span className="text-sm text-slate-600">
                  {formData.client_logo_url ? "Change logo" : "Upload client logo"}
                </span>
              </label>
            </div>
            {formData.client_logo_url && (
              <div className="flex items-center gap-2">
                <img 
                  src={formData.client_logo_url.startsWith('http') ? formData.client_logo_url : `${API.replace('/api', '')}${formData.client_logo_url}`}
                  alt="Client logo" 
                  className="h-10 w-auto object-contain border rounded"
                />
                <button
                  type="button"
                  onClick={() => handleInputChange("client_logo_url", "")}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Logo will appear on the cover slide alongside the RESILIGENT logo when branding is applied.
          </p>
        </div>
      </div>
    </div>
  );

  // Step 2: Challenge Description (Main diagnostic input)
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-slate-800">Client Challenge</h2>
        <p className="text-slate-500 mt-1">Describe what the client is facing in their own words</p>
      </div>

      {/* Challenge Categories for Guidance - Multiple Selection */}
      <div className="mb-6">
        <Label className="mb-3 block text-sm text-slate-600">
          Select relevant challenge areas (you can choose more than one):
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {entrySignals.map((category) => {
            const IconComponent = CATEGORY_ICONS[category.id] || HelpCircle;
            const isSelected = selectedCategories.includes(category.id);
            return (
              <label
                key={category.id}
                htmlFor={`challenge-${category.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  isSelected
                    ? "bg-indigo-50 border-indigo-300"
                    : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                <Checkbox
                  id={`challenge-${category.id}`}
                  checked={isSelected}
                  onCheckedChange={() => handleCategoryToggle(category.id)}
                  className="data-[state=checked]:bg-indigo-600"
                  data-testid={`challenge-checkbox-${category.id}`}
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <IconComponent className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-indigo-600" : "text-slate-400"}`} />
                  <span className={`text-sm truncate ${isSelected ? "text-indigo-700 font-medium" : "text-slate-600"}`}>
                    {category.label}
                  </span>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Example Phrases Panel - Show for all selected categories */}
      {selectedCategories.length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">
              Example phrases (click to add):
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedCategories.map(catId => (
                CHALLENGE_EXAMPLES[catId]?.map((example, idx) => (
                  <button
                    key={`${catId}-${idx}`}
                    onClick={() => handleExampleClick(example)}
                    className="block w-full text-left text-sm text-slate-600 hover:text-indigo-600 hover:bg-white rounded px-3 py-2 transition-all"
                  >
                    &ldquo;{example}&rdquo;
                  </button>
                ))
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Challenge Input */}
      <div>
        <Label className="mb-2 block">
          Describe the client&apos;s challenge *
          <span className="text-slate-400 font-normal ml-2">
            (Use their language - what did they actually say?)
          </span>
        </Label>
        <Textarea
          value={formData.challenge_description}
          onChange={(e) => handleInputChange("challenge_description", e.target.value)}
          placeholder="e.g., 'Our leadership team is not stepping up. We promoted strong technical performers but they are struggling with people management. There is conflict between departments and engagement scores are dropping...'"
          className="min-h-[160px]"
          data-testid="challenge-description-input"
        />
        <p className="text-xs text-slate-400 mt-2">
          The AI will analyse this to determine the appropriate diagnostic approach and products
        </p>
      </div>

      {/* Seniority Level - Multi-select with checkboxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="mb-2 block">Seniority Level(s) Involved *</Label>
          <div className="space-y-2 p-3 border rounded-lg bg-slate-50">
            {SENIORITY_LEVELS.map((level) => {
              const isSelected = selectedSeniority.includes(level.value);
              const isOrganisationWide = level.value === "organisation_wide";
              return (
                <label
                  key={level.value}
                  htmlFor={`seniority-${level.value}`}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-all ${
                    isSelected
                      ? "bg-indigo-100"
                      : "hover:bg-white"
                  } ${isOrganisationWide ? "border-t pt-3 mt-2" : ""}`}
                >
                  <Checkbox
                    id={`seniority-${level.value}`}
                    checked={isSelected}
                    onCheckedChange={() => handleSeniorityToggle(level.value)}
                    className="data-[state=checked]:bg-indigo-600"
                    data-testid={`seniority-checkbox-${level.value}`}
                  />
                  <span className={`text-sm ${isSelected ? "text-indigo-700 font-medium" : "text-slate-600"} ${isOrganisationWide ? "font-medium" : ""}`}>
                    {level.label}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Select all levels that should be included in this intervention
          </p>
        </div>
        <div>
          <Label>Buyer Type (Optional)</Label>
          <Select
            value={formData.buyer_type}
            onValueChange={(v) => handleInputChange("buyer_type", v)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select buyer type" />
            </SelectTrigger>
            <SelectContent>
              {BUYER_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">
            Tailors language and framing in the proposal
          </p>
        </div>
      </div>
    </div>
  );

  // Step 3: Budget & Timeline
  const renderStep3 = () => {
    const budgetRanges = getBudgetRanges(formData.currency);
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-slate-800">Project Parameters</h2>
          <p className="text-slate-500 mt-1">Budget and timeline context (optional)</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label>Currency</Label>
            <Select
              value={formData.currency}
              onValueChange={(v) => {
                handleInputChange("currency", v);
                handleInputChange("budget_range", ""); // Reset budget when currency changes
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((curr) => (
                  <SelectItem key={curr.value} value={curr.value}>
                    {curr.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Budget Range (Quick Select)</Label>
            <Select
              value={formData.budget_range}
              onValueChange={(v) => handleInputChange("budget_range", v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                {budgetRanges.map((range) => (
                  <SelectItem key={range} value={range}>
                    {range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Detailed Pricing Section */}
        <div className="p-4 border rounded-lg bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="text-base font-medium text-slate-800">Pricing Details</Label>
              <p className="text-xs text-slate-500 mt-1">Add specific pricing line items or detailed pricing structure for the Investment section</p>
            </div>
          </div>
          
          {/* Line Items */}
          <div className="space-y-3 mb-4">
            {(formData.pricing_line_items || []).map((item, idx) => (
              <div key={idx} className="space-y-1.5" data-testid="pricing-line-item">
                <div className="flex gap-3 items-start">
                <div className="flex-1">
                  <Input
                    value={item.description}
                    onChange={(e) => {
                      const updated = [...formData.pricing_line_items];
                      updated[idx].description = e.target.value;
                      updated[idx].provenance = null; // hand-edited => lineage lost, fails loud
                      handleInputChange("pricing_line_items", updated);
                    }}
                    placeholder="e.g., CulturePulse™ Diagnostic (up to 50 participants)"
                    className="text-sm"
                  />
                </div>
                <div className="w-40">
                  <Input
                    value={item.amount}
                    onChange={(e) => {
                      const updated = [...formData.pricing_line_items];
                      updated[idx].amount = e.target.value;
                      updated[idx].provenance = null; // hand-typed number => source unknown
                      handleInputChange("pricing_line_items", updated);
                    }}
                    placeholder={`e.g., ${formData.currency === 'NGN' ? '₦' : '$'}500,000`}
                    className="text-sm"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const updated = formData.pricing_line_items.filter((_, i) => i !== idx);
                    handleInputChange("pricing_line_items", updated);
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                >
                  <X className="w-4 h-4" />
                </Button>
                </div>
                {/* Item 13 — every figure released to a client shows its lineage. A hand-typed
                    amount reads "source unknown — do not send unchecked"; one pulled from the
                    confirmed rate card carries its version + effective date. */}
                {(item.description || item.amount) && (
                  <div className="pl-1" data-testid="pricing-line-provenance">
                    <FigureWithProvenance figure={{ value: item.amount || "—", provenance: item.provenance || { state: "unknown" } }} />
                  </div>
                )}
              </div>
            ))}

            {/* Pull a priced line from the confirmed rate card (carries provenance) */}
            {rateCard?.active && (rateCard.rate_card?.modules?.length || rateCard.rate_card?.bundles?.length) ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5" data-testid="rate-card-picker">
                <div className="mb-1.5 text-xs font-medium text-emerald-800">Pull from rate card {rateCard.version} (effective {rateCard.effective_date})</div>
                <div className="flex flex-wrap gap-1.5">
                  {["modules", "bundles", "modular_pricing"].flatMap((b) => (rateCard.rate_card[b] || []).map((it) => (
                    <button key={`${b}-${it.name}`} type="button" data-testid="rate-card-pill"
                      disabled={it.amount == null}
                      onClick={() => {
                        const rcCcy = rateCard.rate_card.currency || "";
                        if (rcCcy && formData.currency && rcCcy !== formData.currency) {
                          toast.warning(`Rate card is in ${rcCcy} but this proposal is in ${formData.currency} — the pulled figure keeps its ${rcCcy} amount; convert it before sending.`);
                        }
                        const updated = [...(formData.pricing_line_items || []), {
                          description: it.name + (it.unit ? ` (${it.unit})` : ""),
                          amount: `${rcCcy} ${it.amount}`.trim(),
                          provenance: { state: "good", source: "rate card",
                            rate_card_version: rateCard.version, effective_date: rateCard.effective_date },
                        }];
                        handleInputChange("pricing_line_items", updated);
                      }}
                      className="rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-xs text-emerald-800 hover:bg-emerald-100 disabled:opacity-40">
                      {it.name} · {it.amount != null ? `${rateCard.rate_card.currency || ""} ${it.amount}` : "unpriced"}
                    </button>
                  )))}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400" data-testid="rate-card-none">
                {rateCard && !rateCard.active
                  ? "No confirmed rate card — any amount typed here has no lineage and will show as “source unknown”."
                  : "Rate card unavailable."}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const updated = [...(formData.pricing_line_items || []), { description: "", amount: "", provenance: null }];
                handleInputChange("pricing_line_items", updated);
              }}
              className="w-full border-dashed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Pricing Line Item
            </Button>
          </div>

          {/* Free-form pricing details */}
          <div>
            <Label className="text-sm text-slate-600">Additional Pricing Notes</Label>
            <Textarea
              value={formData.pricing_details}
              onChange={(e) => handleInputChange("pricing_details", e.target.value)}
              placeholder="Add any additional pricing details, terms, or structure...

Example:
- Per participant rate: ₦25,000
- Minimum cohort: 20 participants
- Includes: Report, Debrief, Action Planning Session
- Travel and accommodation billed separately"
              className="mt-1 min-h-[120px] text-sm"
            />
          </div>
        </div>

        <div>
          <Label>Timeline</Label>
          <Select
            value={formData.timeline}
            onValueChange={(v) => handleInputChange("timeline", v)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select timeline" />
            </SelectTrigger>
            <SelectContent>
              {TIMELINES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Additional Notes</Label>
          <Textarea
            value={formData.additional_notes}
            onChange={(e) => handleInputChange("additional_notes", e.target.value)}
            placeholder="Any additional context, constraints, or specific requirements..."
            className="mt-1 min-h-[100px]"
          />
        </div>

        {/* Complimentary Pilot - confidence signal, not discount */}
        {formData.proposal_type === "pilot" && (
          <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
            <Label className="text-amber-800 font-medium">Pilot Investment</Label>
            <p className="text-xs text-amber-600 mb-3">Complimentary pilots are offered as a confidence signal. The proposal will show full value to establish the engagement's worth.</p>
            <div className="flex items-center gap-4">
              <Select
                value={formData.discount_percentage?.toString() || "100"}
                onValueChange={(v) => handleInputChange("discount_percentage", parseInt(v))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Investment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Full Investment</SelectItem>
                  <SelectItem value="100">Complimentary</SelectItem>
                </SelectContent>
              </Select>
              {formData.discount_percentage === 100 && (
                <span className="text-sm text-amber-700 font-medium">Complimentary Pilot (Confidence Signal)</span>
              )}
            </div>
          </div>
        )}

        {/* RESILIGENT Contact for this Engagement */}
        <div className="space-y-4">
          <div>
            <Label className="text-base font-medium text-slate-800">RESILIGENT Contact</Label>
            <p className="text-xs text-slate-500 mt-1">Who from RESILIGENT should the client contact about this proposal?</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Contact Name</Label>
              <Input
                value={formData.resiligent_contact_name}
                onChange={(e) => handleInputChange("resiligent_contact_name", e.target.value)}
                placeholder="e.g., Remi Dairo"
                className="mt-1"
                data-testid="resiligent-contact-name"
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={formData.resiligent_contact_title}
                onChange={(e) => handleInputChange("resiligent_contact_title", e.target.value)}
                placeholder="e.g., Managing Partner"
                className="mt-1"
                data-testid="resiligent-contact-title"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={formData.resiligent_contact_email}
                onChange={(e) => handleInputChange("resiligent_contact_email", e.target.value)}
                placeholder="e.g., remi@resiligent.com"
                className="mt-1"
                data-testid="resiligent-contact-email"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.resiligent_contact_phone}
                onChange={(e) => handleInputChange("resiligent_contact_phone", e.target.value)}
                placeholder="e.g., +234 800 000 0000"
                className="mt-1"
                data-testid="resiligent-contact-phone"
              />
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-100">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Proposal Summary
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Client:</span>{" "}
                <span className="font-medium">{formData.client_name || "-"}</span>
              </div>
              <div>
                <span className="text-slate-500">Country:</span>{" "}
                <span className="font-medium">
                  {COUNTRIES.find(c => c.value === formData.country)?.label || "-"}
                  {isAfricanCountry(formData.country) && (
                    <Badge variant="outline" className="ml-2 text-xs">Africa</Badge>
                  )}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Industry:</span>{" "}
                <span className="font-medium">{formData.industry || "-"}</span>
              </div>
              <div>
                <span className="text-slate-500">Seniority:</span>{" "}
                <span className="font-medium">
                  {SENIORITY_LEVELS.find(l => l.value === formData.seniority_level)?.label || "-"}
                </span>
              </div>
              {selectedCategories.length > 0 && (
                <div className="col-span-2">
                  <span className="text-slate-500">Challenge Areas:</span>{" "}
                  <span className="font-medium">
                    {selectedCategories.map(c => 
                      entrySignals.find(e => e.id === c)?.label
                    ).filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </div>
            {formData.challenge_description && (
              <div className="mt-4 pt-4 border-t border-indigo-100">
                <span className="text-slate-500 text-sm">Challenge Preview:</span>
                <p className="text-sm text-slate-700 mt-1 line-clamp-2">
                  &ldquo;{formData.challenge_description.substring(0, 150)}...&rdquo;
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Step 4: Review Recommendations (NEW - allows unticking products)
  const renderStep4 = () => {
    if (!diagnosisPreview) return null;

    const isAfrican = isAfricanCountry(formData.country);

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-slate-800">Review Recommendations</h2>
          <p className="text-slate-500 mt-1">
            Review the diagnostic analysis and select which products to include
          </p>
        </div>

        {/* Diagnosis Summary */}
        <Card className="border-indigo-200 bg-indigo-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Diagnostic Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500 block">Primary Issue:</span>
                <Badge className="mt-1 bg-indigo-600">
                  {diagnosisPreview.primary_category?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500 block">Scope:</span>
                <Badge variant="outline" className="mt-1">
                  {diagnosisPreview.diagnostic_scope?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500 block">Commercial Posture:</span>
                <Badge 
                  variant="outline" 
                  className={`mt-1 ${
                    diagnosisPreview.commercial_posture === "bundle_confidently" 
                      ? "border-green-500 text-green-700"
                      : diagnosisPreview.commercial_posture === "slow_client_down"
                      ? "border-amber-500 text-amber-700"
                      : "border-blue-500 text-blue-700"
                  }`}
                >
                  {diagnosisPreview.commercial_posture?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
            </div>

            {/* Root Causes */}
            {diagnosisPreview.likely_root_causes?.length > 0 && (
              <div>
                <span className="text-sm text-slate-500 block mb-2">Likely Root Causes:</span>
                <div className="flex flex-wrap gap-2">
                  {diagnosisPreview.likely_root_causes.map((cause, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-white">
                      {cause.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* African Context Notice */}
        {isAfrican && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">African Context Detected</p>
                  <p className="text-sm text-blue-700 mt-1">
                    DISC LENS Africa will be used instead of standard DISC LENS. This calibrated version 
                    improves diagnostic accuracy by accounting for cultural interpretation factors 
                    (hierarchy norms, collectivism, communication styles) without lowering leadership expectations.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Selection - User can untick */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              Recommended Diagnostics
            </CardTitle>
            <CardDescription>
              Untick any products you wish to exclude from the proposal. 
              Excluded products will be listed with rationale.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {diagnosisPreview.recommended_diagnostics?.map((product, idx) => {
                const isSelected = selectedProducts.includes(product.name);
                const productId = `product-${idx}-${product.name.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <label
                    key={idx}
                    htmlFor={productId}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? "bg-white border-indigo-200 hover:border-indigo-300"
                        : "bg-slate-50 border-slate-200 opacity-60 hover:opacity-80"
                    }`}
                  >
                    <Checkbox
                      id={productId}
                      checked={isSelected}
                      onCheckedChange={() => handleProductToggle(product.name)}
                      className="mt-1 data-[state=checked]:bg-indigo-600"
                      data-testid={`product-checkbox-${product.name.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">
                          {isAfrican && product.name === "DISC Lens" 
                            ? "DISC LENS Africa" 
                            : product.full_name || product.name}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {product.priority || "Primary"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {product.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Selection Summary */}
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {selectedProducts.length} of {diagnosisPreview.recommended_diagnostics?.length || 0} products selected
              </span>
              {selectedProducts.length === 0 && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Select at least one product
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Warnings */}
        {diagnosisPreview.warnings?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <span className="font-medium text-amber-800">Considerations:</span>
                  <ul className="text-sm text-amber-700 mt-2 space-y-1">
                    {diagnosisPreview.warnings.map((warning, idx) => (
                      <li key={idx}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Exclusions from Rules */}
        {diagnosisPreview.exclusions?.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" />
                Deliberately Excluded (Policy)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-slate-600 space-y-2">
                {diagnosisPreview.exclusions.map((exc, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-slate-400">—</span>
                    <div>
                      <span className="font-medium">{exc.disallowed?.join(", ")}</span>
                      <span className="text-slate-500 ml-1">({exc.reason})</span>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Step 5: Proposal Preview (was Step 4)
  const renderStep5 = () => {
    if (!proposal) return null;

    // 9-SECTION STRUCTURE (Master Rebuild)
    // This is the mandatory structure for all proposals
    const sections = [
      { key: "executive_summary", label: "Executive Summary", icon: Building },
      { key: "about_resiligent", label: "About RESILIGENT", icon: Building2 },
      { key: "why_this_matters_now", label: "Why This Matters Now", icon: AlertTriangle },
      { key: "from_assumption_to_evidence", label: "From Assumption to Evidence", icon: Shield },
      { key: "our_approach", label: "Our Approach", icon: Target },
      { key: "what_you_will_receive", label: `What ${proposal.client_name || 'You'} Will Receive`, icon: FileText },
      { key: "timeline_and_phasing", label: "Timeline & Phasing", icon: Clock },
      { key: "investment", label: "Investment & Commercial Considerations", icon: DollarSign },
      { key: "closing_and_contact", label: "Closing & Contact", icon: User },
      { key: "internal_sales_notes", label: "Internal Sales Notes", icon: Briefcase, internal: true },
    ].filter(section => {
      // Only show sections that have content
      const content = proposal.content?.[section.key];
      if (!content) return false;
      if (typeof content === 'object' && content.content) return true;
      if (section.key === 'internal_sales_notes' && typeof content === 'object') return true;
      return false;
    });
    
    // Helper function to extract content from structured format
    const getContentValue = (content, key) => {
      const val = content?.[key];
      if (!val) return null;
      // Structured format: { heading, visual_direction, content }
      if (typeof val === 'object' && val.content) {
        return val.content;
      }
      // Internal sales notes format
      if (key === 'internal_sales_notes' && typeof val === 'object') {
        const notes = [];
        if (val.product_recommendations?.length) {
          notes.push(`Product Recommendations:\n${val.product_recommendations.map(p => `- ${p}`).join('\n')}`);
        }
        if (val.pricing_rationale) {
          notes.push(`\nPricing Rationale:\n${val.pricing_rationale}`);
        }
        if (val.talking_points?.length) {
          notes.push(`\nTalking Points:\n${val.talking_points.map(p => `- ${p}`).join('\n')}`);
        }
        if (val.risks_and_considerations?.length) {
          notes.push(`\nRisks & Considerations:\n${val.risks_and_considerations.map(p => `- ${p}`).join('\n')}`);
        }
        if (val.expansion_opportunities?.length) {
          notes.push(`\nExpansion Opportunities:\n${val.expansion_opportunities.map(p => `- ${p}`).join('\n')}`);
        }
        return notes.join('\n') || null;
      }
      return null;
    };

    // Download proposal as text file
    const handleDownloadText = () => {
      const content = proposal.content || {};
      const diagnosis = proposal.diagnosis || {};
      
      // Build text content
      let textContent = `PROPOSAL FOR ${proposal.client_name?.toUpperCase() || 'CLIENT'}\n`;
      textContent += `${"=".repeat(60)}\n\n`;
      textContent += `Prepared for: ${proposal.contact_person || ''}\n`;
      textContent += `Industry: ${proposal.industry || ''}\n`;
      textContent += `Date: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}\n\n`;
      textContent += `${"=".repeat(60)}\n\n`;
      
      // Add diagnosis summary
      if (diagnosis.primary_category) {
        textContent += `DIAGNOSTIC SUMMARY\n`;
        textContent += `${"-".repeat(40)}\n`;
        textContent += `Primary Issue: ${diagnosis.primary_category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}\n`;
        textContent += `Scope: ${diagnosis.diagnostic_scope?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}\n`;
        if (diagnosis.recommended_diagnostics?.length > 0) {
          textContent += `Recommended Products: ${diagnosis.recommended_diagnostics.map(p => p.full_name || p.name).join(', ')}\n`;
        }
        textContent += `\n`;
      }
      
      // Add each section
      sections.forEach(({ key, label }) => {
        const sectionContent = getContentValue(content, key);
        if (sectionContent) {
          textContent += `${label.toUpperCase()}\n`;
          textContent += `${"-".repeat(40)}\n`;
          textContent += `${sectionContent}\n\n`;
        }
      });
      
      // Add footer
      textContent += `${"=".repeat(60)}\n`;
      textContent += `Prepared by RESILIGENT\n`;
      textContent += `www.resiligent.com\n`;
      
      // Create and download file
      const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Proposal_${proposal.client_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Client'}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Proposal downloaded");
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Proposal for {proposal.client_name}
            </h2>
            <p className="text-slate-500">Review and edit before exporting</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" onClick={() => setStep(1)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Input
            </Button>
            <Button variant="outline" onClick={handleDownloadText}>
              <Download className="w-4 h-4 mr-2" />
              Download Text
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleQuickExport}
              disabled={pollingGamma || exporting}
            >
              {pollingGamma || exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {exporting ? "Starting..." : "Generating..."}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Generate Presentation
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleOpenCloneModal}>
              <Copy className="w-4 h-4 mr-2" />
              Clone for Other Clients
            </Button>
          </div>
        </div>

        {/* Client Logo Section - Important for branding */}
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
                  {proposal.client_logo_url ? (
                    <img 
                      src={`${API.replace('/api', '')}${proposal.client_logo_url}`} 
                      alt="Client logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-800">Client Logo</p>
                  <p className="text-sm text-slate-500">
                    {proposal.client_logo_url 
                      ? "Logo will appear on branded presentation" 
                      : "Upload logo to include in branded presentation"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {proposal.client_logo_url && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Logo Set
                  </Badge>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append("file", file);
                      
                      try {
                        const response = await fetch(`${API}/proposals/${proposal.proposal_id}/upload-logo`, {
                          method: "POST",
                          credentials: "include",
                          body: formData
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          toast.success("Client logo uploaded successfully");
                          // Refresh proposal
                          fetchProposal(proposal.proposal_id);
                        } else {
                          toast.error("Failed to upload logo");
                        }
                      } catch (error) {
                        toast.error("Failed to upload logo");
                      }
                    }}
                  />
                  <Button variant="outline" size="sm" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-1" />
                      {proposal.client_logo_url ? "Change Logo" : "Upload Logo"}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gamma Status - Right after proposal title */}
        {proposal.gamma_url && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-800">Gamma Presentation Ready</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(proposal.gamma_url, "_blank")}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  {proposal.gamma_pdf_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(proposal.gamma_pdf_url, "_blank")}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  )}
                  {proposal.branded_pptx_url && (
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => window.open(`${API}/proposals/${proposal.proposal_id}/download-branded-pptx`, "_blank")}
                      data-testid="download-branded-pptx-btn"
                    >
                      <Palette className="w-4 h-4 mr-1" />
                      Download Branded PPTX
                    </Button>
                  )}
                  {/* Apply Branding button - shows when Gamma PPTX exists but no branded version */}
                  {proposal.gamma_pptx_url && !proposal.branded_pptx_url && (
                    <Button
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={async () => {
                        try {
                          toast.loading("Applying RESILIGENT branding...");
                          const response = await fetch(`${API}/proposals/${proposal.proposal_id}/trigger-branding`, {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" }
                          });
                          toast.dismiss();
                          if (response.ok) {
                            const result = await response.json();
                            toast.success("Branding applied successfully!");
                            // Refresh proposal data
                            fetchProposal(proposal.proposal_id);
                          } else {
                            toast.error("Failed to apply branding");
                          }
                        } catch (error) {
                          toast.dismiss();
                          toast.error("Error applying branding");
                        }
                      }}
                      data-testid="apply-branding-btn"
                    >
                      <Palette className="w-4 h-4 mr-1" />
                      Apply RESILIGENT Branding
                    </Button>
                  )}
                </div>
              </div>
              {proposal.branded_pptx_url && (
                <p className="text-xs text-emerald-700 mt-2">
                  ✓ RESILIGENT branding automatically applied
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Diagnosis Summary Card */}
        {proposal.diagnosis && (
          <Card className="border-indigo-200 bg-indigo-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Diagnostic Analysis
              </CardTitle>
              <CardDescription>
                AI-powered analysis of the client challenge
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 block">Primary Issue:</span>
                  <Badge className="mt-1 bg-indigo-600">
                    {proposal.diagnosis.primary_category?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500 block">Scope:</span>
                  <Badge variant="outline" className="mt-1">
                    {proposal.diagnosis.diagnostic_scope?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500 block">Posture:</span>
                  <Badge 
                    variant="outline" 
                    className={`mt-1 ${
                      proposal.diagnosis.commercial_posture === "bundle_confidently" 
                        ? "border-green-500 text-green-700"
                        : proposal.diagnosis.commercial_posture === "slow_client_down"
                        ? "border-amber-500 text-amber-700"
                        : "border-blue-500 text-blue-700"
                    }`}
                  >
                    {proposal.diagnosis.commercial_posture?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500 block">Products:</span>
                  <span className="text-sm font-medium">
                    {proposal.diagnosis.recommended_diagnostics?.length || 0} recommended
                  </span>
                </div>
              </div>

              {/* Recommended Products */}
              <div>
                <span className="text-sm text-slate-500 block mb-2">Recommended Diagnostics:</span>
                <div className="flex flex-wrap gap-2">
                  {proposal.diagnosis.recommended_diagnostics?.map((product, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-white">
                      {product.full_name || product.name}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Warnings */}
              {proposal.diagnosis.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-amber-800">Considerations:</span>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1">
                        {proposal.diagnosis.warnings.map((warning, idx) => (
                          <li key={idx}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Proposal Sections */}
        <div className="space-y-4">
          {sections.map(({ key, label, icon: Icon, internal }) => (
            <Card key={key} className={internal ? "border-amber-200 bg-amber-50" : ""}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg flex items-center gap-2 ${internal ? "text-amber-800" : ""}`}>
                    <Icon className={`w-5 h-5 ${internal ? "text-amber-600" : "text-indigo-600"}`} />
                    {label}
                    {internal && (
                      <Badge variant="outline" className="ml-2 text-xs border-amber-400 text-amber-700">
                        Internal Only
                      </Badge>
                    )}
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditSection(key)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {editingSection === key ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="min-h-[200px]"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveSection}>
                        <Save className="w-4 h-4 mr-1" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingSection(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={`prose prose-sm max-w-none whitespace-pre-wrap ${internal ? "text-amber-900" : "text-slate-600"}`}>
                    {getContentValue(proposal.content, key) || (
                      <span className="text-slate-400 italic">No content generated</span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.client_name && formData.contact_person && formData.country;
      case 2:
        return formData.challenge_description && formData.challenge_description.length > 20;
      case 3:
        return true;
      case 4:
        return selectedProducts.length > 0;
      default:
        return true;
    }
  };

  return (
    <div className="space-y-6" data-testid="proposal-generator-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Intelligent Proposal Generator
          </h1>
          <p className="text-slate-500 mt-1">
            AI-powered proposals using RESILIGENT&apos;s diagnostic methodology
          </p>
        </div>
        {/* Show different button based on context */}
        {proposal ? (
          <Button
            variant="outline"
            onClick={() => {
              navigate('/proposals');
              setProposal(null);
              setStep(1);
              setShowProposalsList(true);
            }}
            data-testid="create-new-proposal-btn"
          >
            + Create New Proposal
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowProposalsList(!showProposalsList)}
            data-testid="toggle-proposals-list-btn"
          >
            {showProposalsList ? "Create New Proposal" : `View Proposals (${existingProposals.length})`}
          </Button>
        )}
      </div>

      {/* Existing Proposals List */}
      {showProposalsList && existingProposals.length > 0 && (
        <Card className="p-4">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Your Proposals</h2>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {existingProposals.map((p) => (
              <div
                key={p.proposal_id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid={`proposal-item-${p.proposal_id}`}
              >
                <div 
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => navigate(`/proposals/${p.proposal_id}`)}
                >
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Building className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{p.client_name}</p>
                    <p className="text-xs text-slate-500">
                      {p.proposal_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} • 
                      {p.industry} • 
                      {new Date(p.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.gamma_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(p.gamma_url, "_blank");
                      }}
                      title="View in Gamma"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                  {p.branded_pptx_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`${API}/proposals/${p.proposal_id}/download-branded-pptx`, "_blank");
                      }}
                      title="Download Branded PPTX"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  {p.gamma_url && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full hidden sm:inline-block">
                      Gamma
                    </span>
                  )}
                  {p.branded_pptx_url && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full hidden sm:inline-block">
                      Branded
                    </span>
                  )}
                  <ChevronRight 
                    className="w-4 h-4 text-slate-400 cursor-pointer" 
                    onClick={() => navigate(`/proposals/${p.proposal_id}`)}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Show generator form when not viewing proposals list or when editing */}
      {(!showProposalsList || proposalId) && (
        <>
          {/* Progress Steps - Now 4 steps before final proposal */}
          {step < 5 && (
            <div className="flex items-center justify-center gap-2 mb-8">
              {[
                { num: 1, label: "Client" },
                { num: 2, label: "Challenge" },
                { num: 3, label: "Parameters" },
                { num: 4, label: "Review" },
              ].map((s) => (
                <div key={s.num} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center font-medium transition-all text-sm ${
                        s.num === step
                          ? "bg-indigo-600 text-white"
                          : s.num < step
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {s.num < step ? <CheckCircle className="w-4 h-4" /> : s.num}
                    </div>
                    <span className={`text-xs mt-1 ${s.num === step ? "text-indigo-600 font-medium" : "text-slate-400"}`}>
                      {s.label}
                    </span>
                  </div>
                  {s.num < 4 && (
                    <div
                      className={`w-8 h-1 mx-1 mb-5 ${
                        s.num < step ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Main Content */}
          <Card className="p-6">
            {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}

        {/* Navigation */}
        {step < 5 && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => {
                if (step === 4) {
                  setDiagnosisPreview(null);
                  setSelectedProducts([]);
                }
                setStep((s) => Math.max(1, s - 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={step === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {step === 1 || step === 2 ? (
              <Button
                onClick={() => {
                  setStep((s) => s + 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={!canProceed()}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : step === 3 ? (
              <Button
                onClick={handleAnalyseChallenge}
                disabled={analysingChallenge || !canProceed()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {analysingChallenge ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analysing Challenge...
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Analyse and Review
                  </>
                )}
              </Button>
            ) : step === 4 ? (
              <Button
                onClick={handleGenerateProposal}
                disabled={generating || !canProceed()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Proposal...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Proposal
                  </>
                )}
              </Button>
            ) : null}
          </div>
        )}
      </Card>

      {/* Gamma Export Dialog */}
      <Dialog open={showGammaDialog} onOpenChange={setShowGammaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Setup Gamma Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>One-time setup required:</strong> Configure your Gamma template ID in Settings.
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-slate-600">
                1. Go to <strong>Settings</strong> page
              </p>
              <p className="text-sm text-slate-600">
                2. Find the <strong>Gamma.app Integration</strong> section
              </p>
              <p className="text-sm text-slate-600">
                3. Enter your Gamma template ID and save
              </p>
              <p className="text-sm text-slate-600">
                4. Come back here and click Export - it will work automatically
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGammaDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => navigate("/settings")}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Go to Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Proposal Dialog */}
      <Dialog open={showCloneModal} onOpenChange={setShowCloneModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-indigo-600" />
              Clone Proposal to Other Clients
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Clone this proposal to other clients in <strong>{formData.industry || proposal?.industry}</strong>. 
              The proposal content will be adapted with the new client name.
            </p>
            
            {loadingClients ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : industryClients.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Building className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No other clients found in this industry.</p>
                <p className="text-sm">Add more clients to use this feature.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <p className="text-xs text-slate-500 uppercase font-semibold mb-2">
                  Select clients ({cloneTargetClients.length} selected)
                </p>
                {industryClients.map((client) => {
                  const isSelected = cloneTargetClients.includes(client.client_id);
                  return (
                    <label
                      key={client.client_id}
                      htmlFor={`clone-client-${client.client_id}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? "bg-indigo-50 border-indigo-300"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <Checkbox
                        id={`clone-client-${client.client_id}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleClientSelection(client.client_id)}
                        className="data-[state=checked]:bg-indigo-600"
                      />
                      <div className="flex items-center gap-3 flex-1">
                        {client.logo_url ? (
                          <img
                            src={`${API.replace('/api', '')}${client.logo_url}`}
                            alt=""
                            className="w-8 h-8 object-contain rounded"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                            <Building className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-slate-800">{client.company_name}</span>
                          {client.country && (
                            <span className="text-xs text-slate-500 ml-2">{client.country}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloneModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCloneProposal}
              disabled={cloning || cloneTargetClients.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {cloning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cloning...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Clone to {cloneTargetClients.length} Client{cloneTargetClients.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Template Upload Dialog */}
      <Dialog open={showBrandTemplateDialog} onOpenChange={setShowBrandTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-indigo-600" />
              Apply RESILIGENT Branding
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2 font-medium">Workflow Steps:</p>
              <ol className="text-sm text-blue-700 space-y-1.5 list-decimal list-inside">
                <li>Generate your presentation in Gamma (use the &quot;Export to Gamma&quot; button above)</li>
                <li>In Gamma: Click Share → Download → PowerPoint (.pptx)</li>
                <li>Upload the downloaded PPTX file below</li>
                <li>Click &quot;Apply & Download&quot; to get your branded presentation</li>
              </ol>
            </div>
            
            {proposal?.gamma_url && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(proposal.gamma_url, "_blank")}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Gamma Presentation
              </Button>
            )}

            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-300 transition-colors">
              <input
                type="file"
                accept=".pptx"
                onChange={(e) => setBrandTemplateFile(e.target.files?.[0] || null)}
                className="hidden"
                id="brand-template-file-input"
                data-testid="brand-template-file-input"
              />
              <label htmlFor="brand-template-file-input" className="cursor-pointer">
                {brandTemplateFile ? (
                  <div className="space-y-2">
                    <FileText className="w-10 h-10 text-indigo-600 mx-auto" />
                    <p className="text-sm font-medium text-slate-800">{brandTemplateFile.name}</p>
                    <p className="text-xs text-slate-500">Click to change file</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto" />
                    <p className="text-sm font-medium text-slate-600">
                      Upload your Gamma PPTX file
                    </p>
                    <p className="text-xs text-slate-400">Only .pptx files accepted</p>
                  </div>
                )}
              </label>
            </div>

            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-600">
                <strong>What branding applies:</strong> RESILIGENT branded cover overlay on slide 1, 
                client logo and RESILIGENT logo at the bottom, and page numbers on all slides except first and last.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowBrandTemplateDialog(false);
                setBrandTemplateFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyBrandTemplate}
              disabled={applyingBrandTemplate || !brandTemplateFile}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="apply-brand-template-submit"
            >
              {applyingBrandTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying Branding...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Apply & Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
